import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Copy, KeyRound, Wallet, Ticket, CalendarDays, LogOut, ShoppingBag, CircleDollarSign, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import RewardPoster from "@/components/RewardPoster";
import KingPoster from "@/components/KingPoster";
import UserAvatar from "@/components/UserAvatar";

const maskName = (n) => (n && n.length === 11 && n.startsWith("1") ? `${n.slice(0, 3)}****${n.slice(7)}` : n);

const BADGE_STYLES = {
  inactive: "text-gray-400 border-gray-600",
  normal: "text-gray-300 border-gray-600",
  vip: "text-[#D4AF37] border-[#D4AF37]/60",
  svip: "text-[#070709] border-transparent bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11]",
};

const WD_STATUS = {
  pending: { label: "审核中", cls: "text-yellow-400 border-yellow-500/40" },
  paid: { label: "已打款", cls: "text-emerald-400 border-emerald-500/40" },
  rejected: { label: "已拒绝", cls: "text-red-400 border-red-500/40" },
};

const shortAddr = (a) => (a && a.length > 16 ? `${a.slice(0, 8)}...${a.slice(-6)}` : a);

export default function Me() {
  const { user, ready, openAuth, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [cardKey, setCardKey] = useState("");
  const [usdt, setUsdt] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [posterSub, setPosterSub] = useState(null);
  const [posterOpen, setPosterOpen] = useState(false);
  const [bonuses, setBonuses] = useState([]);
  const [kingOpen, setKingOpen] = useState(false);

  const latestKing = useMemo(() => bonuses.find((b) => b.period_type?.startsWith("submit_")) || null, [bonuses]);

  useEffect(() => {
    const fresh = submissions.find((s) => s.status === "approved" && !localStorage.getItem(`hy_celebrated_${s.sid}`));
    if (fresh) {
      localStorage.setItem(`hy_celebrated_${fresh.sid}`, "1");
      setPosterSub(fresh);
      setPosterOpen(true);
      return;
    }
    if (latestKing && !localStorage.getItem(`hy_king_${latestKing.period_key}`) && !posterOpen) {
      localStorage.setItem(`hy_king_${latestKing.period_key}`, "1");
      setKingOpen(true);
    }
  }, [submissions, latestKing, posterOpen]);

  const load = () => {
    api.get("/invite/stats").then((r) => {
      setStats(r.data);
      setUsdt(r.data.usdt_address || "");
    }).catch(() => {});
    api.get("/invite/records").then((r) => setRecords(r.data)).catch(() => {});
    api.get("/withdraw/records").then((r) => setWithdrawals(r.data)).catch(() => {});
    api.get("/submissions/mine").then((r) => setSubmissions(r.data)).catch(() => {});
    api.get("/bonuses/mine").then((r) => setBonuses(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!ready) return <div className="min-h-screen" />;

  if (!user) {
    return (
      <main className="min-h-screen grid place-items-center px-5 pt-16">
        <div className="text-center">
          <KeyRound className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif-sc text-3xl font-bold mb-4">请先登录</h1>
          <p className="text-gray-400 text-sm mb-8">登录会员账号后查看个人中心</p>
          <button data-testid="me-login-btn" onClick={() => openAuth({ mode: "login" })} className="btn-gold px-8 py-3 text-sm">
            会员登录
          </button>
        </div>
      </main>
    );
  }

  const redeem = async (e) => {
    e.preventDefault();
    if (!cardKey.trim()) return toast.error("请输入卡密");
    setBusy(true);
    try {
      await api.post("/keys/redeem", { card_key: cardKey.trim() });
      await refresh();
      toast.success("卡密激活成功，已解锁邀请奖励与提现功能");
      setCardKey("");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const saveUsdt = async () => {
    setBusy(true);
    try {
      await api.put("/wallet/usdt", { usdt_address: usdt.trim() });
      toast.success("USDT收款地址已绑定");
      load();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const submitWithdraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/withdraw", { amount: Number(amount) });
      toast.success(
        data.fee_amount > 0
          ? `提现申请已提交：¥${Number(amount).toLocaleString()}，手续费 ¥${data.fee_amount}，实收 ¥${data.net_amount} ≈ ${data.usdt_amount} USDT`
          : `提现申请已提交：¥${Number(amount).toLocaleString()} ≈ ${data.usdt_amount} USDT，审核后打款`
      );
      setAmount("");
      load();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const changePwd = async () => {
    if (!oldPwd || !newPwd) return toast.error("请输入原密码和新密码");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success("密码修改成功");
      setOldPwd("");
      setNewPwd("");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const memberTags = [];
  if (stats?.is_activated) {
    if (stats.invite_count >= 30) memberTags.push("社群领袖");
    else if (stats.invite_count >= 10) memberTags.push("推广精英");
    else if (stats.invite_count >= 1) memberTags.push("推荐官");
    if (stats.bonus_total > 0) memberTags.push("榜首得主");
  }
  if (submissions.some((s) => s.status === "approved")) memberTags.push("投稿达人");
  if (withdrawals.some((w) => w.status === "paid")) memberTags.push("提现先锋");

  return (
    <main className="min-h-screen pt-28 pb-24 px-5 lg:px-8" data-testid="personal-center-page">
      <div className="mx-auto max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#D4AF37] mb-4">MEMBER CENTER · 个人中心</p>
          <h1 className="font-serif-sc text-3xl sm:text-4xl font-bold tracking-tight mb-12">会员档案</h1>
        </motion.div>

        <div className="panel p-8 lg:p-10 mb-8 gold-glow">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="relative w-24 h-24 shrink-0 grid place-items-center" data-testid="avatar-frame">
              <div className={`w-20 h-20 p-0.5 bg-[#0F0F14] border ${user.level === "svip" ? "border-[#D4AF37]/70" : "border-[#D4AF37]/40"}`}>
                <UserAvatar name={user.username} size={76} className="w-full h-full" />
              </div>
              {user.level === "svip" && (
                <>
                  {[
                    ["top-0 left-0 border-t-2 border-l-2", 0],
                    ["top-0 right-0 border-t-2 border-r-2", 0.5],
                    ["bottom-0 left-0 border-b-2 border-l-2", 1],
                    ["bottom-0 right-0 border-b-2 border-r-2", 1.5],
                  ].map(([pos, delay], i) => (
                    <motion.span
                      key={i}
                      data-testid={i === 0 ? "svip-corner-frame" : undefined}
                      className={`absolute w-6 h-6 border-[#F3E5AB] pointer-events-none ${pos}`}
                      animate={{ opacity: [0.35, 1, 0.35], scale: [1, 1.15, 1] }}
                      transition={{ duration: 2.2, repeat: Infinity, delay, ease: "easeInOut" }}
                    />
                  ))}
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "conic-gradient(from 0deg, transparent 72%, rgba(212,175,55,0.3) 88%, transparent)" }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  />
                </>
              )}
              {memberTags.includes("榜首得主") && (
                <motion.div
                  data-testid="avatar-crown"
                  className="absolute -top-4 left-1/2 -translate-x-1/2 text-[#D4AF37]"
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Crown className="w-5 h-5 drop-shadow-[0_0_6px_rgba(212,175,55,0.8)]" fill="#D4AF37" />
                </motion.div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h2 className="font-serif-sc text-2xl font-bold font-mono-num">{user.username}</h2>
                <span
                  data-testid="membership-badge"
                  className={`text-[11px] tracking-[0.2em] px-3 py-1 border ${BADGE_STYLES[user.level] || BADGE_STYLES.normal}`}
                >
                  {user.level_name}
                </span>
              </div>
              {memberTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="member-tags">
                  {memberTags.map((t) => (
                    <span
                      key={t}
                      data-testid={`member-tag-${t}`}
                      className="text-[10px] tracking-[0.2em] px-2.5 py-1 border border-[#D4AF37]/30 text-[#D4AF37]/90 bg-[#D4AF37]/5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 font-mono-num">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> {(user.created_at || "").slice(0, 10)} 加入
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Ticket className="w-3.5 h-3.5" /> 卡密 {user.card_key || "未激活"}
                </span>
              </div>
            </div>
            <button
              data-testid="me-logout-btn"
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="btn-ghost-gold px-5 py-2.5 text-xs inline-flex items-center gap-2 self-start sm:self-center"
            >
              <LogOut className="w-3.5 h-3.5" /> 退出登录
            </button>
          </div>
        </div>

        {latestKing && (
          <div className="panel p-6 mb-8 gold-glow flex flex-wrap items-center gap-4" data-testid="king-banner">
            <Crown className="w-6 h-6 text-[#D4AF37]" fill="#D4AF37" />
            <p className="text-sm text-gray-300 flex-1 min-w-52">
              恭喜您加冕 <span className="font-mono-num text-[#F3E5AB]">{latestKing.period_key}</span>{" "}
              {latestKing.period_type === "submit_week" ? "周榜" : "月度"}投稿王，
              额外奖励 <span className="font-mono-num text-[#D4AF37]">¥{latestKing.amount.toLocaleString()}</span> 已到账
            </p>
            <button data-testid="king-poster-btn" onClick={() => setKingOpen(true)} className="btn-gold px-5 py-2.5 text-xs whitespace-nowrap">
              生成投稿王专属海报
            </button>
          </div>
        )}

        {!user.is_activated && (
          <div className="panel p-8 lg:p-10 mb-8 gold-glow" data-testid="activation-panel">
            <div className="h-px shimmer-line mb-8" />
            <h3 className="font-serif-sc text-xl font-bold text-center mb-2">激活卡密 · 成为正式会员</h3>
            <p className="text-sm text-gray-400 text-center mb-8">
              激活后进入项目交流群，每日分享日赚500+稳定项目，并解锁 ¥128/人 邀请奖励与USDT提现功能
            </p>
            <form onSubmit={redeem} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto mb-4">
              <input
                data-testid="me-card-key-input"
                value={cardKey}
                onChange={(e) => setCardKey(e.target.value)}
                placeholder="HY-XXXX-XXXX-XXXX"
                className="input-gold flex-1 px-4 py-3 text-sm font-mono-num tracking-widest uppercase text-center"
              />
              <button data-testid="me-redeem-btn" type="submit" disabled={busy} className="btn-gold px-8 py-3 text-sm whitespace-nowrap">
                立即激活
              </button>
            </form>
            <p className="text-xs text-gray-500 text-center">
              没有卡密？
              <Link to="/card-guide" data-testid="me-card-guide-link" className="text-[#D4AF37] underline underline-offset-4 decoration-[#D4AF37]/40 hover:decoration-[#D4AF37] transition-colors">
                查看图文获取教程
              </Link>
              <span className="mx-1.5 text-gray-700">·</span>淘宝搜索「合赢项目交流社区」卡密购买<span className="mx-1.5 text-gray-700">·</span>测试卡密 <span className="font-mono-num">HY-SVIP-9999-9999</span>
            </p>
          </div>
        )}

        {user.is_activated && stats && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#D4AF37]/10 border border-[#D4AF37]/10 mb-8">
              {[
                { label: "累计邀请奖励", value: stats.reward_balance, testid: "total-earned-display", note: [stats.bonus_total > 0 ? `榜首奖励 ¥${stats.bonus_total.toLocaleString()}` : null, stats.submit_total > 0 ? `投稿奖励 ¥${stats.submit_total.toLocaleString()}` : null].filter(Boolean).join(" · ") || null },
                { label: "可提现余额", value: stats.available_balance, testid: "reward-balance-display", note: null },
                { label: "审核中 / 已打款", value: stats.held_balance, testid: "held-balance-display", note: null },
              ].map((s) => (
                <div key={s.label} className="bg-[#0A0A0E] p-6">
                  <p className="text-xs text-gray-500 tracking-widest mb-2 flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-[#D4AF37]" /> {s.label}
                  </p>
                  <p className="font-mono-num text-3xl text-gold-grad font-semibold" data-testid={s.testid}>
                    ¥ {s.value.toLocaleString()}
                  </p>
                  {s.note && <p className="mt-1 text-[10px] tracking-widest text-[#D4AF37]/70">{s.note}</p>}
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="panel p-8">
                <p className="text-xs text-gray-500 tracking-widest mb-2 flex items-center gap-2">
                  <CircleDollarSign className="w-4 h-4 text-[#D4AF37]" /> USDT 收款地址（TRC20）
                </p>
                <p className="text-xs text-gray-600 mb-4">提现将以 USDT 打款至该地址，仅支持 TRC20 网络（T 开头 34 位）</p>
                <input
                  data-testid="me-usdt-input"
                  value={usdt}
                  onChange={(e) => setUsdt(e.target.value)}
                  placeholder="T 开头的 TRC20 地址，共 34 位"
                  className="input-gold w-full px-4 py-3 text-xs font-mono-num mb-4"
                />
                <button data-testid="bind-usdt-btn" onClick={saveUsdt} disabled={busy} className="btn-ghost-gold px-6 py-2.5 text-sm w-full">
                  保存绑定
                </button>
              </div>
              <div className="panel p-8">
                <p className="text-xs text-gray-500 tracking-widest mb-2">申请提现</p>
                <p className="text-xs text-gray-600 mb-4" data-testid="withdraw-rate-hint">
                  单笔最低 ¥100 · 当前汇率 1 USDT ≈ ¥{Number(stats.usdt_rate || 7.2).toFixed(2)}
                  {stats.withdraw_fee_enabled && Number(stats.withdraw_fee_rate) > 0 && ` · 手续费 ${stats.withdraw_fee_rate}%`}
                </p>
                <input
                  data-testid="me-withdraw-amount-input"
                  type="number"
                  min="100"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`可提现 ¥${stats.available_balance.toLocaleString()}`}
                  className="input-gold w-full px-4 py-3 text-sm font-mono-num mb-2"
                />
                <p className="text-xs font-mono-num text-[#D4AF37] mb-4 min-h-4" data-testid="withdraw-usdt-preview">
                  {Number(amount) > 0 && (() => {
                    const feeRate = stats.withdraw_fee_enabled ? Number(stats.withdraw_fee_rate || 0) : 0;
                    const fee = +(Number(amount) * feeRate / 100).toFixed(2);
                    const net = +(Number(amount) - fee).toFixed(2);
                    const usdt = (net / Number(stats.usdt_rate || 7.2)).toFixed(2);
                    return fee > 0
                      ? `手续费 ¥${fee.toLocaleString()} · 实收 ¥${net.toLocaleString()} ≈ ${usdt} USDT`
                      : `≈ ${usdt} USDT`;
                  })()}
                </p>
                <button data-testid="withdraw-btn" onClick={submitWithdraw} disabled={busy} className="btn-gold px-6 py-2.5 text-sm w-full">
                  提交提现申请
                </button>
              </div>
            </div>

            <div className="panel p-8 mb-8">
              <h3 className="font-serif-sc text-lg font-bold mb-6">提现记录（{withdrawals.length}）</h3>
              {withdrawals.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="me-withdraw-empty">暂无提现记录</p>
              ) : (
                <div className="divide-y divide-[#D4AF37]/10" data-testid="me-withdraw-list">
                  {withdrawals.map((w, i) => (
                    <div key={i} className="py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <span className="font-mono-num text-[#F3E5AB]">¥{w.amount.toLocaleString()}</span>
                      {w.usdt_amount != null && (
                        <span className="font-mono-num text-xs text-[#D4AF37]">
                          {w.fee_amount > 0 ? `实收 ¥${w.net_amount.toLocaleString()} ≈ ${w.usdt_amount} USDT` : `≈ ${w.usdt_amount} USDT`}
                        </span>
                      )}
                      <span className="font-mono-num text-xs text-gray-500 flex-1">{shortAddr(w.usdt_address)}</span>
                      <span className={`text-[11px] tracking-widest px-2.5 py-1 border ${WD_STATUS[w.status]?.cls || ""}`}>
                        {WD_STATUS[w.status]?.label || w.status}
                      </span>
                      <span className="text-xs text-gray-600 font-mono-num">{(w.created_at || "").slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="panel p-8 mb-8">
          <p className="text-xs text-gray-500 tracking-widest mb-2">我的邀请码</p>
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono-num text-3xl text-[#F3E5AB] tracking-widest" data-testid="my-invite-code">
              {user.invite_code}
            </span>
            <button
              data-testid="copy-invite-code-btn"
              onClick={() => {
                navigator.clipboard.writeText(user.invite_code).then(() => toast.success("邀请码已复制"));
              }}
              className="text-gray-500 hover:text-[#D4AF37] transition-colors"
              aria-label="复制邀请码"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            好友通过您的邀请链接或邀请码注册，即计入您的邀请进度并发放 ¥128 奖励。
          </p>
        </div>

        <div className="panel p-8 mb-8">
          <h3 className="font-serif-sc text-lg font-bold mb-6 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#D4AF37]" /> 修改密码
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="password"
              data-testid="change-password-old-input"
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              placeholder="原密码"
              className="input-gold flex-1 px-4 py-3 text-sm"
            />
            <input
              type="password"
              data-testid="change-password-new-input"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="新密码（至少6位）"
              className="input-gold flex-1 px-4 py-3 text-sm"
            />
            <button
              data-testid="change-password-submit-btn"
              onClick={changePwd}
              disabled={busy}
              className="btn-gold px-6 py-3 text-sm whitespace-nowrap"
            >
              确认修改
            </button>
          </div>
        </div>

        <div className="panel p-8 mb-8">
          <h3 className="font-serif-sc text-lg font-bold mb-2">我的投稿（{submissions.length}）</h3>
          <p className="text-xs text-gray-500 mb-6">首页「今日项目」区可提交项目，平台审核评估录用奖励最高 ¥188,888</p>
          {submissions.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="my-submissions-empty">暂无投稿</p>
          ) : (
            <div className="divide-y divide-[#D4AF37]/10" data-testid="my-submissions-list">
              {submissions.map((s, i) => (
                <div key={s.sid} className="py-3 flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-gray-200 flex-1">{s.title}</span>
                  <span
                    className={`text-[11px] tracking-widest px-2.5 py-1 border ${
                      s.status === "pending"
                        ? "text-yellow-400 border-yellow-500/40"
                        : s.status === "approved"
                          ? "text-emerald-400 border-emerald-500/40"
                          : "text-red-400 border-red-500/40"
                    }`}
                  >
                    {s.status === "pending" ? "审核评估中" : s.status === "approved" ? `已录用 +¥${(s.reward ?? 188888).toLocaleString()}` : "未通过"}
                  </span>
                  {s.status === "approved" && (
                    <button
                      data-testid={`submission-poster-btn-${i}`}
                      onClick={() => {
                        setPosterSub(s);
                        setPosterOpen(true);
                      }}
                      className="text-xs text-[#070709] bg-[#D4AF37] px-3 py-1.5 hover:bg-[#E5C158] transition-colors"
                    >
                      喜报海报
                    </button>
                  )}
                  <span className="text-xs text-gray-600 font-mono-num">{(s.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <RewardPoster
          open={posterOpen}
          onClose={() => setPosterOpen(false)}
          submission={posterSub}
          username={maskName(user.username)}
          inviteCode={user.invite_code}
        />
        <KingPoster
          open={kingOpen}
          onClose={() => setKingOpen(false)}
          bonus={latestKing}
          username={maskName(user.username)}
          inviteCode={user.invite_code}
        />

        <div className="panel p-8">
          <h3 className="font-serif-sc text-lg font-bold mb-6">邀请明细（{records.length}）</h3>
          {records.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="me-records-empty">暂无邀请记录</p>
          ) : (
            <div className="divide-y divide-[#D4AF37]/10" data-testid="me-records-list">
              {records.map((r, i) => (
                <div key={i} className="py-3 flex items-center justify-between text-sm">
                  <span className="text-gray-300 font-mono-num">{r.invitee_username}</span>
                  <span className="font-mono-num text-[#D4AF37]">+¥{r.reward}</span>
                  <span className="text-xs text-gray-600 font-mono-num">{(r.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
